import { useContext, useMemo } from 'react'

import { MobileBottomNavigation } from '@hhgtech/hhg-components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { Scale, Community } from '@hhgtech/icons/other'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useSpacingSubotStickyBar } from 'hooks/useSpacingSubotStickyBar'
import { LOCALE } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { CommonStyle } from './commonStyles'
import { StyledMobileBottomNavigation } from './index.styled'

const MobileBottomHealthToolNavigation = ({
  isResult = false,
  topicLink,
  onReadMoreClick,
}: {
  isResult?: boolean
  topicLink?: string
  onReadMoreClick?(): void
}) => {
  const { locale = 'vi-VN', pathname } = useRouter()
  const { formatMessage: f } = useIntl()
  const {
    state: { currentDomain },
  } = useContext(AppContext)
  const currentUrl = useCurrentUrl()
  useSpacingSubotStickyBar()
  const haveCommunity = useMemo(() => {
    return LOCALE_HAS_COMMUNITY_SITE.includes(locale as LOCALE)
  }, [locale])

  return (
    <>
      <StyledMobileBottomNavigation isCollapsed={false}>
        <a
          href={currentDomain}
          className="pointer-event-child-none"
          data-event-category="Sticky Bottom"
          data-event-action="Home Click"
          data-event-label={currentDomain + '/categories'}
        >
          <MobileBottomNavigation.Icon
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M8.8 10.6248C8.45 10.6248 8.18733 10.4665 8.012 10.1498C7.83733 9.83314 7.84167 9.5248 8.025 9.2248L11.225 3.9998C11.4083 3.6998 11.6667 3.5498 12 3.5498C12.3333 3.5498 12.5917 3.6998 12.775 3.9998L15.975 9.2248C16.1583 9.5248 16.1627 9.83314 15.988 10.1498C15.8127 10.4665 15.55 10.6248 15.2 10.6248L8.8 10.6248ZM17.5 21.6248C16.35 21.6248 15.375 21.2248 14.575 20.4248C13.775 19.6248 13.375 18.6498 13.375 17.4998C13.375 16.3498 13.775 15.3748 14.575 14.5748C15.375 13.7748 16.35 13.3748 17.5 13.3748C18.65 13.3748 19.625 13.7748 20.425 14.5748C21.225 15.3748 21.625 16.3498 21.625 17.4998C21.625 18.6498 21.225 19.6248 20.425 20.4248C19.625 21.2248 18.65 21.6248 17.5 21.6248ZM4.275 21.1248C4.025 21.1248 3.81267 21.0375 3.638 20.8628C3.46267 20.6875 3.375 20.4665 3.375 20.1998L3.375 14.7748C3.375 14.5248 3.46267 14.3121 3.638 14.1368C3.81267 13.9621 4.03333 13.8748 4.3 13.8748H9.725C9.975 13.8748 10.1877 13.9621 10.363 14.1368C10.5377 14.3121 10.625 14.5331 10.625 14.7998V20.2248C10.625 20.4748 10.5377 20.6875 10.363 20.8628C10.1877 21.0375 9.96667 21.1248 9.7 21.1248H4.275ZM17.5 20.1248C18.2333 20.1248 18.8543 19.8708 19.363 19.3628C19.871 18.8541 20.125 18.2331 20.125 17.4998C20.125 16.7665 19.871 16.1455 19.363 15.6368C18.8543 15.1288 18.2333 14.8748 17.5 14.8748C16.7667 14.8748 16.146 15.1288 15.638 15.6368C15.1293 16.1455 14.875 16.7665 14.875 17.4998C14.875 18.2331 15.1293 18.8541 15.638 19.3628C16.146 19.8708 16.7667 20.1248 17.5 20.1248ZM4.875 19.6248H9.125V15.3748H4.875V19.6248ZM9.85 9.1248H14.15L12 5.6498L9.85 9.1248Z"
                  fill="#595959"
                />
              </svg>
            }
            label={f({
              id: 'mobileBar.home',
              defaultMessage: 'Home',
            })}
            isActive={pathname === '/'}
          />
        </a>

        <a
          href={currentDomain + '/health-tools'}
          className="pointer-event-child-none"
          data-event-category="Sticky Bottom"
          data-event-action="Engage Click"
          data-event-label={currentDomain + '/health-tools'}
        >
          <MobileBottomNavigation.Icon
            icon={<Scale isActive={true} />}
            label={f({
              id: 'mobileBar.healthTools',
              defaultMessage: 'Health Tools',
            })}
            isActive={true}
          />
        </a>

        {haveCommunity && (
          <a
            href={
              isResult
                ? currentDomain + topicLink
                : currentDomain + '/community'
            }
            className="pointer-event-child-none"
            data-event-category="Sticky Bottom"
            data-event-action="Together Click"
            data-event-label={
              isResult
                ? currentDomain + topicLink
                : currentDomain + '/community'
            }
          >
            <MobileBottomNavigation.Icon
              icon={
                isResult ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M2.8 21.175L7.175 8.9L15.075 16.8L2.8 21.175ZM5.275 18.7L12.325 16.2L7.775 11.65L5.275 18.7ZM13.975 12.7L13.15 11.875L18.625 6.375C19.125 5.875 19.7333 5.625 20.45 5.625C21.1667 5.625 21.775 5.875 22.275 6.375L22.75 6.875L21.925 7.7L21.45 7.225C21.1833 6.94167 20.854 6.8 20.462 6.8C20.0707 6.8 19.7417 6.94167 19.475 7.225L13.975 12.7ZM10.325 9.05L9.5 8.2L10.1 7.6C10.3833 7.31667 10.525 6.96233 10.525 6.537C10.525 6.11233 10.3833 5.75 10.1 5.45L9.45 4.825L10.3 3.975L10.925 4.6C11.4583 5.13333 11.725 5.775 11.725 6.525C11.725 7.275 11.4583 7.91667 10.925 8.45L10.325 9.05ZM12.15 10.875L11.325 10.05L14.8 6.55C15.0667 6.28333 15.2 5.954 15.2 5.562C15.2 5.17067 15.0667 4.84167 14.8 4.575L13.325 3.1L14.15 2.25L15.65 3.725C16.15 4.24167 16.4 4.854 16.4 5.562C16.4 6.27067 16.15 6.88333 15.65 7.4L12.15 10.875ZM15.8 14.525L14.975 13.7L16.175 12.5C16.7083 11.95 17.3583 11.675 18.125 11.675C18.8917 11.675 19.5417 11.95 20.075 12.5L21.275 13.7L20.425 14.525L19.225 13.325C18.925 13.025 18.5543 12.875 18.113 12.875C17.671 12.875 17.3 13.025 17 13.325L15.8 14.525Z"
                      fill="#595959"
                    />
                  </svg>
                ) : (
                  <Community />
                )
              }
              label={f({
                id: 'mobileBar.community',
                defaultMessage: 'Community',
              })}
            />
          </a>
        )}
        {onReadMoreClick && (
          <MobileBottomNavigation.Icon
            onClick={onReadMoreClick}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5.3 20.5C4.8 20.5 4.375 20.325 4.025 19.975C3.675 19.625 3.5 19.2 3.5 18.7V5.3C3.5 4.8 3.675 4.375 4.025 4.025C4.375 3.675 4.8 3.5 5.3 3.5H18.7C19.2 3.5 19.625 3.675 19.975 4.025C20.325 4.375 20.5 4.8 20.5 5.3V18.7C20.5 19.2 20.325 19.625 19.975 19.975C19.625 20.325 19.2 20.5 18.7 20.5H5.3ZM5.3 19H18.7C18.7667 19 18.8333 18.9667 18.9 18.9C18.9667 18.8333 19 18.7667 19 18.7V5.3C19 5.23333 18.9667 5.16667 18.9 5.1C18.8333 5.03333 18.7667 5 18.7 5H5.3C5.23333 5 5.16667 5.03333 5.1 5.1C5.03333 5.16667 5 5.23333 5 5.3V18.7C5 18.7667 5.03333 18.8333 5.1 18.9C5.16667 18.9667 5.23333 19 5.3 19ZM8.05 16.775C8.3 16.775 8.51267 16.6873 8.688 16.512C8.86267 16.3373 8.95 16.125 8.95 15.875C8.95 15.625 8.86267 15.4167 8.688 15.25C8.51267 15.0833 8.3 15 8.05 15C7.8 15 7.59167 15.0833 7.425 15.25C7.25833 15.4167 7.175 15.625 7.175 15.875C7.175 16.125 7.25833 16.3373 7.425 16.512C7.59167 16.6873 7.8 16.775 8.05 16.775ZM8.05 12.875C8.3 12.875 8.51267 12.7917 8.688 12.625C8.86267 12.4583 8.95 12.25 8.95 12C8.95 11.75 8.86267 11.5417 8.688 11.375C8.51267 11.2083 8.3 11.125 8.05 11.125C7.8 11.125 7.59167 11.2083 7.425 11.375C7.25833 11.5417 7.175 11.75 7.175 12C7.175 12.25 7.25833 12.4583 7.425 12.625C7.59167 12.7917 7.8 12.875 8.05 12.875ZM8.05 9C8.3 9 8.51267 8.91667 8.688 8.75C8.86267 8.58333 8.95 8.375 8.95 8.125C8.95 7.875 8.86267 7.66233 8.688 7.487C8.51267 7.31233 8.3 7.225 8.05 7.225C7.8 7.225 7.59167 7.31233 7.425 7.487C7.25833 7.66233 7.175 7.875 7.175 8.125C7.175 8.375 7.25833 8.58333 7.425 8.75C7.59167 8.91667 7.8 9 8.05 9ZM11.85 16.625H16.05C16.25 16.625 16.425 16.5543 16.575 16.413C16.725 16.271 16.8 16.0917 16.8 15.875C16.8 15.675 16.725 15.5 16.575 15.35C16.425 15.2 16.25 15.125 16.05 15.125H11.85C11.6333 15.125 11.454 15.2 11.312 15.35C11.1707 15.5 11.1 15.675 11.1 15.875C11.1 16.0917 11.1707 16.271 11.312 16.413C11.454 16.5543 11.6333 16.625 11.85 16.625ZM11.85 12.75H16.05C16.25 12.75 16.425 12.679 16.575 12.537C16.725 12.3957 16.8 12.2167 16.8 12C16.8 11.7833 16.725 11.604 16.575 11.462C16.425 11.3207 16.25 11.25 16.05 11.25H11.85C11.6333 11.25 11.454 11.3207 11.312 11.462C11.1707 11.604 11.1 11.7833 11.1 12C11.1 12.2167 11.1707 12.3957 11.312 12.537C11.454 12.679 11.6333 12.75 11.85 12.75ZM11.85 8.875H16.05C16.25 8.875 16.425 8.8 16.575 8.65C16.725 8.5 16.8 8.325 16.8 8.125C16.8 7.90833 16.725 7.72933 16.575 7.588C16.425 7.446 16.25 7.375 16.05 7.375H11.85C11.6333 7.375 11.454 7.446 11.312 7.588C11.1707 7.72933 11.1 7.90833 11.1 8.125C11.1 8.325 11.1707 8.5 11.312 8.65C11.454 8.8 11.6333 8.875 11.85 8.875ZM5 19V5V19Z"
                  fill="#595959"
                />
              </svg>
            }
            label={f({
              id: 'article.ampReadmore',
              defaultMessage: 'Read more',
            })}
            data-event-category="Sticky Bottom"
            data-event-action="See More Click"
            data-event-label={currentUrl}
            className="pointer-event-child-none"
          />
        )}
      </StyledMobileBottomNavigation>
      <style>{CommonStyle}</style>
    </>
  )
}

export default MobileBottomHealthToolNavigation
